import type { RefObject } from 'react'
import { useEffect, useRef } from 'react'

/**
 * [BP-2378] Confirms the BP-2376 mobile scroll-trap fix actually works in the
 * field: fires mobile_scroll_past_manifest once a buyer who has seen the
 * manifest section scrolls the page past it. Mirrors FiltersTopBar's
 * IntersectionObserver "exit viewport" technique.
 */
export function useMobileScrollPastManifestTracking({
  containerRef,
  enabled,
  onScrollPast,
}: {
  containerRef: RefObject<HTMLElement | null> | null
  enabled: boolean
  onScrollPast: () => void
}) {
  const hasBeenVisibleRef = useRef(false)
  const hasFiredRef = useRef(false)

  useEffect(() => {
    if (!enabled) return
    const el = containerRef?.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return
        if (entry.isIntersecting) {
          hasBeenVisibleRef.current = true
          return
        }
        // Only counts as "scrolled past" (not just scrolled away from below,
        // e.g. before the user ever reached it) once it's been seen, and
        // only when it exited above the viewport (scrolled down past it).
        if (
          hasBeenVisibleRef.current &&
          !hasFiredRef.current &&
          entry.boundingClientRect.top < 0
        ) {
          hasFiredRef.current = true
          onScrollPast()
          observer.disconnect()
        }
      },
      { threshold: 0 }
    )
    observer.observe(el)

    return () => observer.disconnect()
  }, [containerRef, enabled, onScrollPast])
}
